import { Button, Divider } from '@ds';
import { useCallback, useState } from 'react';
import { v4 as uuidV4 } from 'uuid';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { AttachmentsForm } from '@features/modals/CaseAttachmentsModal/types';
import { FormLinkDescriptionElement, ConditionalRender } from '@ui';
import { MAX_LINKS, MAX_LINK_DESCRIPTION_LENGTH } from '@features/forms/CaseForms/schema';
import { adjustEditingIndexAfterDelete, validateCurrentLink } from '@features/modals/CaseAttachmentsModal/utils';
import { CaseLinkType } from '@/api/apis/case';

export type AttachmentLinkData = { url: string; description: string };
export type AttachmentLinkMutationResult = { links?: CaseLinkType[] } | void | null;

type LinksListProps = {
  initialLinks: CaseLinkType[];
  addLink: (linkData: AttachmentLinkData) => Promise<AttachmentLinkMutationResult>;
  editLink: (linkId: string, linkData: AttachmentLinkData) => Promise<AttachmentLinkMutationResult>;
  deleteLink: (linkId: string) => Promise<AttachmentLinkMutationResult>;
  isPending: boolean;
  disabled?: boolean;
  showUploadBtn?: boolean;
  showDeleteBtn?: boolean;
  showEditBtn?: boolean;
};

export const LinksList = ({
  initialLinks,
  addLink,
  editLink,
  deleteLink,
  isPending,
  disabled,
  showUploadBtn = true,
  showDeleteBtn = true,
  showEditBtn = true,
}: LinksListProps) => {
  const { control } = useFormContext<AttachmentsForm>();
  const [beLinks, setBeLinks] = useState<CaseLinkType[]>(initialLinks);
  const { trigger, getValues, formState } = useFormContext();
  const {
    fields: links,
    append,
    replace,
    remove,
  } = useFieldArray({
    control,
    name: 'links',
    keyName: 'internalUuid',
  });
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [isSavingItem, setIsSavingItem] = useState(false);

  const cannotAddItem = links.length > 0 && !formState.isValid;
  const isMaxLinksReached = links.length >= MAX_LINKS;

  const isDisabled = isMaxLinksReached || disabled || cannotAddItem || isSavingItem || isPending;

  const updateStates = useCallback(
    (responseLinks: CaseLinkType[], id: string, internalUuid: string) => {
      const updatedLink = responseLinks.find((link) => link.id === id);
      if (updatedLink) {
        const updatedLinks = links.map((field) => {
          if (field.internalUuid === internalUuid) {
            return { ...updatedLink, internalUuid };
          }
          if (field.id === updatedLink.id) {
            return { ...field, ...updatedLink };
          }
          return field;
        });
        replace([...updatedLinks]);
      }
      setBeLinks(responseLinks);
    },
    [links, replace],
  );

  const createLink = useCallback(
    async ({ url, description, internalUuid }: CaseLinkType) => {
      const response = await addLink({ url, description });
      const linkNotInInitialState = response?.links?.find(
        (link) => !beLinks.some((initialLink) => initialLink.id === link.id),
      );
      if (response?.links?.length && linkNotInInitialState && internalUuid) {
        updateStates(response.links, linkNotInInitialState.id, internalUuid);
      }
    },
    [addLink, beLinks, updateStates],
  );

  const handleEditLink = useCallback(
    async ({ id, url, description, internalUuid }: CaseLinkType) => {
      const response = await editLink(id, { url, description });
      if (response?.links?.length && internalUuid) {
        updateStates(response.links, id, internalUuid);
      }
    },
    [editLink, updateStates],
  );

  const onLinkSave = useCallback(
    async ({ isNew, ...data }: CaseLinkType) => {
      if (isNew) {
        await createLink(data);
      } else {
        const existingBeLink = beLinks.find((link) => link.id === data.id);
        if (existingBeLink?.url !== data.url || existingBeLink?.description !== data.description) {
          await handleEditLink(data);
        }
      }
    },
    [beLinks, createLink, handleEditLink],
  );

  const handleSetEditingIndex = (nextIndex: number | null) => {
    if (editingIndex !== null && editingIndex !== nextIndex) {
      const currentLink = getValues(`links.${editingIndex}`);
      if (!validateCurrentLink(editingIndex, formState.errors, currentLink, trigger)) {
        return;
      }
    }
    setEditingIndex(nextIndex);
  };

  const onLinkDelete = useCallback(
    async (id: string) => {
      const indexToDelete = links.findIndex((link) => link.id === id);
      if (indexToDelete === -1) return;

      if (beLinks.some((link) => link.id === id)) {
        const response = await deleteLink(id);
        setBeLinks((prev) => response?.links ?? prev.filter((link) => link.id !== id));
      }

      remove(indexToDelete);

      setEditingIndex((prevEditingIndex) =>
        adjustEditingIndexAfterDelete(indexToDelete, prevEditingIndex, links.length - 1),
      );
    },
    [beLinks, deleteLink, links, remove],
  );

  const addLinkItem = async () => {
    if (editingIndex !== null) {
      const data = getValues(`links.${editingIndex}`);
      if (!validateCurrentLink(editingIndex, formState.errors, data, trigger)) {
        return;
      }
      const linkData = links.find((link) => link.id === data.id);
      setIsSavingItem(true);
      try {
        await onLinkSave({ ...linkData, ...data });
      } finally {
        setIsSavingItem(false);
      }
    }

    append({ id: uuidV4(), url: '', description: '', isNew: true });
    setEditingIndex(links.length);
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex-1 min-h-0 overflow-y-auto pr-3 flex flex-col gap-4 py-4">
        {!links.length && <div className="text-xl-sb text-base-20">No items</div>}
        {links.map((linkData, index) => (
          <div className="flex flex-col" key={linkData.internalUuid ?? linkData.id}>
            <FormLinkDescriptionElement
              index={index}
              formFieldName="links"
              handleSave={(data) => onLinkSave({ ...linkData, ...data })}
              handleDelete={() => onLinkDelete(linkData.id)}
              disabled={disabled || !showDeleteBtn}
              isEditing={editingIndex === index}
              setEditingIndex={showEditBtn ? handleSetEditingIndex : undefined}
              maxLength={MAX_LINK_DESCRIPTION_LENGTH}
            />
          </div>
        ))}
      </div>
      <ConditionalRender show={showUploadBtn}>
        <div className="shrink-0 bg-base-80">
          <Divider />
          <div className="py-4 flex justify-end">
            <Button
              size="md"
              isLoading={isSavingItem}
              isDisabled={isDisabled}
              onClick={addLinkItem}
              className="text-m-sb"
              variant="outline"
            >
              + Add Item
            </Button>
          </div>
        </div>
      </ConditionalRender>
    </div>
  );
};
